import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import Devices from "~icons/ph/devices";
import Fingerprint from "~icons/ph/fingerprint";
import Key from "~icons/ph/key";
import type { AuthenticatedSnapshot } from "../../shared/types";
import { useShell } from "../app/shell";
import { sessionsOptions } from "../data/queries";
import {
  useEndOtherSessions,
  useEndSession,
  useRemovePasskey,
  useSubmitting,
} from "../data/mutations";
import { date, day, t } from "../i18n";
import { RecoveryCode } from "../links/guest-account";
import { FormError } from "../ui/components";

type RowHeadProps = {
  icon: typeof Key;
  title: ReactNode;
  detail?: ReactNode;
};

export function RowHead({ icon: Icon, title, detail }: RowHeadProps) {
  return (
    <span className="account-row-head">
      <Icon width={20} height={20} />
      <span className="account-row-text">
        <span className="account-row-title">{title}</span>
        {detail && <span className="account-row-detail">{detail}</span>}
      </span>
    </span>
  );
}

export function AccountRow({
  action,
  children,
  ...head
}: RowHeadProps & { action?: ReactNode; children?: ReactNode }) {
  return (
    <div className="account-row">
      <div className="account-row-main">
        <RowHead {...head} />
        {action}
      </div>
      {children && <div className="account-row-body">{children}</div>}
    </div>
  );
}

// The server names a device it can't identify in English only.
const deviceName = (device: string) =>
  device === "Unknown device" ? t("جهاز غير معروف") : device;

function DevicesSection({
  accountId,
  onEndedOthers,
}: {
  accountId: string;
  onEndedOthers: () => void;
}) {
  const sessions = useQuery(sessionsOptions(accountId));
  const endSession = useEndSession();
  const endOthers = useEndOtherSessions();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  async function end(ending: Promise<unknown>) {
    setError("");
    try {
      await ending;
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const list = [...(sessions.data ?? [])].sort(
    (a, b) => Number(b.current) - Number(a.current),
  );
  return (
    <section className="account-rows">
      <h3 id="your-devices">{t("أجهزتك")}</h3>
      <ul className="account-row-list" aria-labelledby="your-devices">
        {list.map(({ id, device, place, renewed_at, current }) => {
          const name = deviceName(device);
          return (
            <li key={id}>
              <AccountRow
                icon={Devices}
                title={
                  <>
                    <bdi>{name}</bdi>
                    {current && (
                      <span className="session-current">{t("هذا الجهاز")}</span>
                    )}
                  </>
                }
                detail={
                  <>
                    {place && (
                      <>
                        <bdi>{place}</bdi>
                        {" · "}
                      </>
                    )}
                    {t("آخر نشاط: {date}", { date: day(renewed_at, true) })}
                  </>
                }
                action={
                  !current && (
                    <button
                      className="quiet-button"
                      aria-label={t("إخراج {device}، آخر نشاط في {date}", {
                        device: name,
                        date: date(renewed_at),
                      })}
                      disabled={submitting}
                      onClick={() => void end(endSession.mutateAsync(id))}
                    >
                      {t("إخراج")}
                    </button>
                  )
                }
              />
            </li>
          );
        })}
      </ul>
      {list.length > 1 && (
        <button
          className="button secondary small"
          disabled={submitting}
          onClick={() => void end(endOthers.mutateAsync().then(onEndedOthers))}
        >
          {t("إخراج الأجهزة الأخرى")}
        </button>
      )}
      <FormError message={sessions.error?.message ?? error} />
    </section>
  );
}

export function SignInStep({
  account: { me, passkeys },
}: {
  account: AuthenticatedSnapshot;
}) {
  const { recovery } = useShell();
  const removePasskey = useRemovePasskey();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  const [endedOthers, setEndedOthers] = useState(false);
  const offerNewCode = endedOthers && !!me.has_recovery && !recovery.code;
  // A recovery code is shown only until this step is left.
  const { clear } = recovery;
  useEffect(() => clear, [clear]);
  async function remove(id: string) {
    setError("");
    try {
      await removePasskey.mutateAsync(id);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const supported = browserSupportsWebAuthn();
  return (
    <>
      <h2>{t("الدخول والأجهزة")}</h2>
      <section className="account-rows">
        <h3>{t("العودة إلى حسابك")}</h3>
        <ul className="account-row-list">
          {(supported || passkeys.length > 0) && (
            <li>
              <AccountRow
                icon={Fingerprint}
                title={t("مفاتيح المرور")}
                detail={t("الدخول ببصمتك أو وجهك أو قفل الشاشة.")}
                action={
                  supported && (
                    <button
                      className="button secondary small"
                      aria-label={t("إضافة مفتاح مرور")}
                      disabled={submitting}
                      onClick={() => void recovery.addPasskey()}
                    >
                      {t("إضافة")}
                    </button>
                  )
                }
              >
                {passkeys.length > 0 && (
                  <ul className="account-item-list">
                    {passkeys.map(({ id, created_at }) => {
                      const added = date(created_at);
                      return (
                        <li key={id}>
                          {t("أُضيف في {date}", { date: added })}
                          <button
                            className="quiet-button"
                            aria-label={t(
                              "إزالة مفتاح المرور المضاف في {date}",
                              { date: added },
                            )}
                            disabled={submitting}
                            onClick={() => void remove(id)}
                          >
                            {t("إزالة")}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </AccountRow>
            </li>
          )}
          <li>
            <AccountRow
              icon={Key}
              title={
                recovery.code ? t("رمز استعادة الحساب") : t("رمز الاستعادة")
              }
              detail={
                /* Signing out leaves the code as it was, so whoever holds it can return. */
                <span
                  role="status"
                  className={offerNewCode ? "offer" : undefined}
                >
                  {recovery.code
                    ? t(
                        "احتفظ بهذا الرمز في مكان آمن. يتيح لك الدخول من أي جهاز، وإنشاء رمز جديد يلغي السابق.",
                      )
                    : offerNewCode
                      ? t(
                          "خرجت أجهزتك الأخرى، لكن من يملك رمز الاستعادة يستطيع الدخول مجددًا. إذا لم تكن متأكدًا من يملكه، فأنشئ رمزًا جديدًا يحل محله.",
                        )
                      : me.has_recovery
                        ? t("الرمز الجديد يلغي السابق.")
                        : t("احتياطي للدخول من أي جهاز دون مفتاح مرور.")}
                </span>
              }
              action={
                !recovery.code && (
                  <button
                    className="button secondary small"
                    aria-label={
                      me.has_recovery
                        ? t("إنشاء رمز استعادة جديد")
                        : t("حفظ رمز الاستعادة")
                    }
                    disabled={submitting}
                    onClick={() => void recovery.save()}
                  >
                    {me.has_recovery ? t("إنشاء") : t("حفظ")}
                  </button>
                )
              }
            >
              {recovery.code && (
                <RecoveryCode code={recovery.code} username={me.username} />
              )}
            </AccountRow>
          </li>
        </ul>
        <FormError message={recovery.error} />
        <FormError message={error} />
      </section>
      <DevicesSection
        accountId={me.id}
        onEndedOthers={() => setEndedOthers(true)}
      />
    </>
  );
}
